/**
 * Avatar — deterministic initials and tint, with an optional image.
 *
 * The tint comes from `avatarSeed` (the server hands us a stable 0–7) so a
 * person keeps the same colour on every screen, in both themes, forever. With no
 * seed we hash the name, which is stable for the same reason.
 *
 * `MONOGRAM_TEXT_MIX` is how much of that tint survives before `--text-primary`
 * is mixed in. Initials render at 10px in the `xs` size, so they are held to the
 * 4.5:1 floor rather than the 3:1 one — the light theme binds, and
 * `scripts/contrast-check.mjs` asserts every seed in both themes.
 */

import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import * as RAvatar from '@radix-ui/react-avatar';
import { cn } from '@/lib/cn';
import { initials as toInitials } from '@/lib/format';
import { mixSurface, mixText } from './Badge';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

/** Share of the seed tint kept in the monogram before mixing in the theme ink. */
export const MONOGRAM_TEXT_MIX = 64;
/** Share of the tint in the disc fill. */
export const MONOGRAM_FILL_MIX = 22;

/**
 * The disc metric per size. Exported because an *empty* seat — the dashed
 * placeholder an unassigned row draws — has to be the same circle as a filled
 * one, and the only way to guarantee that is for both to read this map rather
 * than for one of them to re-type `size-[22px]`.
 */
export const AVATAR_SIZE: Record<AvatarSize, string> = {
  // 11px, not 10px: the monogram is the identity, so it sits on the same floor
  // as every other chip label in the product.
  // Each size names its line box. `leading-none` on the monogram gave 11px
  // text an 11px line and 13px text a 13px one — two more size/line pairs in
  // every region that draws a face, for no geometric gain: the disc is a fixed
  // square and the glyph is flex-centred inside it either way.
  xs: 'size-[18px] text-[length:var(--text-xs)] leading-[16px]',
  sm: 'size-[22px] text-[length:var(--text-xs)] leading-[16px]',
  md: 'size-7 text-xs leading-[16px]',
  lg: 'size-9 text-base leading-[20px]',
};

/**
 * The same metric as a number, for the one consumer that cannot take the class:
 * `Skeleton` writes `width`/`height` inline, so an inline style would win over
 * `size-[22px]` and a loading disc would silently stop being the disc it stands
 * in for. Sits directly under `AVATAR_SIZE` because the two are one measure —
 * change a size and both lines move together, in view of each other.
 */
export const AVATAR_PX: Record<AvatarSize, number> = {
  xs: 18,
  sm: 22,
  md: 28,
  lg: 36,
};

const SIZE = AVATAR_SIZE;

const OVERLAP: Record<AvatarSize, string> = {
  xs: '-ml-1.5',
  sm: '-ml-2',
  md: '-ml-2.5',
  lg: '-ml-3',
};

/** Stable across reloads and machines — no `Math.random`, no insertion order. */
function hashName(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
}

function hueFor(name: string, seed: number | undefined): string {
  const slot = ((seed ?? hashName(name)) % 8) + 1;
  return `var(--viz-${slot})`;
}

export interface AvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  name: string;
  /** `User.avatarSeed`. Falls back to a hash of the name. */
  seed?: number;
  size?: AvatarSize;
  src?: string | null;
  /** Ring colour class — set it to the surface the avatar sits on when overlapping. */
  ringClassName?: string;
  /**
   * Drop the seed tint for one neutral disc. Dense surfaces pass this: a hashed
   * hue on every face says nothing, and it spends the same eight hues the charts
   * and the stage ramp are already using.
   */
  muted?: boolean;
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { name, seed, size = 'md', src, className, ringClassName, muted = false, style, ...rest },
  ref,
) {
  const hue = hueFor(name, seed);
  const paint = muted
    ? {
        background: 'var(--surface-3)',
        color: 'var(--text-secondary)',
        outline: '1px solid var(--line)',
      }
    : {
        background: mixSurface(hue, MONOGRAM_FILL_MIX),
        color: mixText(hue, MONOGRAM_TEXT_MIX),
        outline: `1px solid ${mixSurface(hue, 30)}`,
      };

  return (
    <RAvatar.Root
      ref={ref}
      role="img"
      aria-label={name}
      title={name}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
        'font-medium select-none',
        SIZE[size],
        ringClassName,
        className,
      )}
      style={{
        // An outline, not an inset box-shadow: `ring-*` owns box-shadow so the
        // overlap ring in AvatarGroup keeps working.
        ...paint,
        outlineOffset: '-1px',
        ...style,
      }}
      {...rest}
    >
      {src ? <RAvatar.Image src={src} alt="" className="size-full object-cover" /> : null}
      <RAvatar.Fallback delayMs={src ? 120 : 0} className="tracking-tight">
        <span aria-hidden="true">{toInitials(name)}</span>
      </RAvatar.Fallback>
    </RAvatar.Root>
  );
});

export interface AvatarPerson {
  id?: string;
  name: string;
  avatarSeed?: number;
  src?: string | null;
}

export interface AvatarGroupProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  users: AvatarPerson[];
  /** How many faces before collapsing to `+N`. */
  max?: number;
  size?: AvatarSize;
  /** Ring colour class — match the surface behind the stack. */
  ringClassName?: string;
}

export const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>(function AvatarGroup(
  { users, max = 4, size = 'sm', ringClassName = 'ring-surface-1', className, ...rest },
  ref,
) {
  const shown = users.slice(0, max);
  const overflow = users.length - shown.length;

  return (
    <div
      ref={ref}
      className={cn('flex items-center', className)}
      title={users.map((user) => user.name).join(', ')}
      {...rest}
    >
      {shown.map((user, index) => (
        <Avatar
          key={user.id ?? `${user.name}-${index}`}
          name={user.name}
          seed={user.avatarSeed}
          src={user.src}
          size={size}
          ringClassName={cn('ring-2', ringClassName, index > 0 && OVERLAP[size])}
        />
      ))}

      {overflow > 0 ? (
        <span
          className={cn(
            'relative inline-flex shrink-0 items-center justify-center rounded-full',
            'bg-surface-3 font-semibold text-muted ring-2 tnum',
            SIZE[size],
            OVERLAP[size],
            ringClassName,
          )}
        >
          +{overflow}
        </span>
      ) : null}
    </div>
  );
});
